import React, {
  useCallback,
  useEffect,
  useRef,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { MapContext } from '@react-google-maps/api';
import IbePropTypes, { coordinateType } from '../../IbePropTypes';

const IMAGE_MARKER_BASE_SIZE = '30px';

const AdvancedMarker = ({
  children,
  icon,
  onClick,
  position,
  title,
  zIndex,
  // Can be passed in via the MarkerClusterer component as parent
  clusterer,
}) => {
  const imageRef = useRef();
  const markerRef = useRef();
  const map = useContext(MapContext);

  // If we have an icon passed in, we use the actual text content of the marker
  // to decide if we should re-render it.
  // If it doesn't have text, we just use the icon.
  // We also track the size as an easy way to see if the marker style has changed
  // e.g. when selecting/unselecting a pin on the map
  const svgElement = icon?.documentElement;
  const markerText = svgElement?.querySelector?.('text')?.innerHTML ?? icon;
  const svgWidth = svgElement?.getAttribute?.('width') ?? 0;
  const svgHeight = svgElement?.getAttribute?.('height') ?? 0;
  const hasClusterer = typeof clusterer?.addMarker === 'function';

  const handleClick = useCallback((event) => onClick(event), [onClick]);

  const getIcon = () => {
    // If we're given a URL (string) then we must create an image in the DOM and
    // reference that.
    // https://developers.google.com/maps/documentation/javascript/advanced-markers/graphic-markers#graphic-glyph
    if (typeof icon === 'string') {
      const glyphImg = document.createElement('img');
      glyphImg.src = icon;
      glyphImg.style.width = IMAGE_MARKER_BASE_SIZE;
      imageRef.current = glyphImg;
      return imageRef.current;
    }

    // Otherwise, if we have an SVG we can just use the documentElement
    // https://developers.google.com/maps/documentation/javascript/advanced-markers/graphic-markers#inline-svg
    return icon?.documentElement;
  };

  useEffect(() => {
    const markerIcon = getIcon();

    const googleMarker = new window.google.maps.marker.AdvancedMarkerElement({
      // For non-clustered markers (e.g. crossings map), attach marker directly to map.
      map: hasClusterer ? undefined : map,
      content: markerIcon,
      position,
      title,
      gmpClickable: true,
      zIndex,
    });

    try {
      /**
       * @TODO - addEventListener is currently not supported in the quarterly version
       * of the Google Maps JS API. When the current weekly version which will be around
       * August. When that time comes we can remove this try and just use the addEventListener
       * call directly
       */
      googleMarker.addEventListener('gmp-click', handleClick);
    } catch (error) {
      googleMarker.addListener('click', handleClick);
    }

    if (hasClusterer) {
      clusterer.addMarker(googleMarker, title);
    }

    markerRef.current = googleMarker;

    return () => {
      if (imageRef.current?.remove) {
        imageRef.current.remove();
      }
      google.maps.event.clearInstanceListeners(googleMarker);
      googleMarker.map = null;
      if (typeof clusterer?.removeMarker === 'function') {
        clusterer.removeMarker(title);
      }
      markerRef.current = null;
    };
  }, []); // adding the dependencies of clusterer, position and handleClick causes map rendering
  // issues. Avoid for now, as it has been working ok in this way.

  useEffect(() => {
    // when the icon updates, e.g. a price loads, we update both the image ref and the marker
    // to use this new image ref
    if (typeof icon === 'string' && imageRef?.current && markerRef?.current) {
      imageRef.current.src = icon;
      markerRef.current.content = imageRef.current;
    }
    if (icon.documentElement && markerRef.current) {
      markerRef.current.content = icon.documentElement;
    }

    return () => {
      if (!icon) {
        if (typeof clusterer?.removeMarker === 'function') {
          clusterer.removeMarker(title);
        }
        google.maps.event.clearInstanceListeners(markerRef.current);
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
  }, [markerText, svgWidth, svgHeight]);

  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { markerRef: { current: markerRef.current } });
    }
    return childrenWithProps;
  });

  return <>{childrenWithProps}</>;
};

AdvancedMarker.propTypes = {
  children: IbePropTypes.children,
  // This component expects an SVG element as the icon
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType, PropTypes.object]),
  onClick: PropTypes.func,
  position: PropTypes.shape(coordinateType).isRequired,
  title: PropTypes.string,
  clusterer: PropTypes.shape({
    addMarker: PropTypes.func,
    removeMarker: PropTypes.func,
  }),
  zIndex: PropTypes.number,
};

AdvancedMarker.defaultProps = {
  children: <></>,
  icon: '',
  onClick: () => {},
  title: '',
  clusterer: null,
  zIndex: 1,
};

export default AdvancedMarker;
